import { FollowButtonOutline, FollowButtonSize, FollowButtonVariant } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { AuthorCardsContainerProps } from "~/components/article/article-author-details/styles";
import { AuthorAvatar } from "~/components/author/author-avatar";
import { AuthorBio } from "~/components/author/author-bio";
import { AuthorHeaderBar } from "~/components/author/author-header-bar";
import { MagazinesStyleVariant } from "~/components/section/section-style/const";
import type { authorCardAuthor$key } from "~/queries/__generated__/authorCardAuthor.graphql";

import {
  AuthorCardContainer,
  AvatarContainer,
  BioContainer,
  FollowButtonContainer,
  HeaderBarContainer,
  NameContainer,
  SocialLinksContainer,
  StyleAuthorCardContainer,
  StyleAuthorInfoContainer,
  StyleAvatarContainer,
  StyledAuthorName,
  StyledAuthorSocialLinks,
  StyledEntityFollowButton,
  StyledStyleAuthorName,
  StyleNameContainer,
} from "./styles";

export type Props = {
  reference: authorCardAuthor$key;
  variant: AuthorCardsContainerProps["$variant"];
};

export const AuthorCard: FunctionComponent<Props> = ({ reference: reference_, variant }) => {
  const author = useFragment(
    graphql`
      fragment authorCardAuthor on Author {
        ...authorSocialLinksAuthor
        ...authorAvatarAuthor
        ...authorNameAuthor
        ...authorBioAuthor
        ...entityFollowButtonBase
      }
    `,
    reference_,
  );

  if (variant === MagazinesStyleVariant) {
    return (
      <StyleAuthorCardContainer>
        <StyleAvatarContainer>
          <AuthorAvatar height={80} reference={author} width={80} placeholder />
        </StyleAvatarContainer>
        <StyleAuthorInfoContainer>
          <StyleNameContainer>
            <StyledStyleAuthorName reference={author} />
            <StyledEntityFollowButton
              outline={FollowButtonOutline.Outlined}
              reference={author}
              variant={FollowButtonVariant.Style}
            />
          </StyleNameContainer>
          <AuthorBio reference={author} variant={MagazinesStyleVariant} />
          <StyledAuthorSocialLinks reference={author} variant={MagazinesStyleVariant} />
        </StyleAuthorInfoContainer>
      </StyleAuthorCardContainer>
    );
  }

  return (
    <AuthorCardContainer>
      <AvatarContainer>
        <AuthorAvatar height={100} reference={author} width={100} placeholder />
      </AvatarContainer>
      <HeaderBarContainer>
        <AuthorHeaderBar />
      </HeaderBarContainer>
      <FollowButtonContainer>
        <StyledEntityFollowButton
          outline={FollowButtonOutline.Outlined}
          reference={author}
          size={FollowButtonSize.Thin}
        />
      </FollowButtonContainer>
      <NameContainer>
        <StyledAuthorName reference={author} />
      </NameContainer>
      <BioContainer>
        <AuthorBio reference={author} />
      </BioContainer>
      <SocialLinksContainer>
        <StyledAuthorSocialLinks reference={author} />
      </SocialLinksContainer>
    </AuthorCardContainer>
  );
};

AuthorCard.displayName = "AuthorCard";
